import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Flame, Plus } from "lucide-react";
import DayMealPlanDialog from "@/components/meals/DayMealPlanDialog";
import MealPlanDetailsDialog from "@/components/meals/MealPlanDetailsDialog";
import { useTimeFormatter } from "@/lib/timeFormat";

const dayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const dayOrder = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const goalLabels = {
  bulking: "Bulking",
  cutting: "Cutting",
  maintenance: "Maintenance",
  clean_eating: "Clean Eating",
};

const goalColors = {
  bulking: "bg-primary/10 text-primary",
  cutting: "bg-destructive/10 text-destructive",
  maintenance: "bg-emerald-500/10 text-emerald-500",
  clean_eating: "bg-amber-500/10 text-amber-500",
};

function to12Hour(time) {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${period}`;
}

export default function ClientWeeklyMealPlanView({ clientId }) {
  const fmtTime = useTimeFormatter();
  const [selectedDay, setSelectedDay] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { data: weeklyMealPlan = null, isLoading: weeklyLoading } = useQuery({
    queryKey: ["userWeeklyMealPlan", clientId],
    queryFn: () =>
      base44.entities.UserWeeklyMealPlan.filter({ user_id: clientId }).then(
        (results) => results[0] || null
      ),
    enabled: !!clientId,
  });

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ["mealPlanTemplates"],
    queryFn: () => base44.entities.MealPlanTemplate.list(),
  });

  const isLoading = weeklyLoading || templatesLoading;

  const weeklyPlans = {};
  dayOrder.forEach((day) => {
    const templateId = weeklyMealPlan?.[day];
    if (templateId) {
      const template = templates.find((t) => t.id === templateId);
      if (template) {
        weeklyPlans[day] = [template];
      } else {
        weeklyPlans[day] = [];
      }
    } else {
      weeklyPlans[day] = [];
    }
  });

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {dayOrder.map((day) => (
          <div key={day} className="h-24 rounded-lg bg-muted/30 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {dayOrder.map((day, idx) => (
          <Card key={day} className="border-0 shadow-sm flex flex-col hover:shadow-md transition-shadow cursor-pointer">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-display">{dayNames[idx].slice(0, 3)}</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              {weeklyPlans[day].length === 0 ? (
                <button
                  onClick={() => handleDayClick(day)}
                  className="text-[10px] text-muted-foreground text-center py-2 flex items-center justify-center hover:text-foreground transition-colors flex-1"
                >
                  <Plus className="w-2 h-2 mr-0.5" /> Add
                </button>
              ) : (
                <div className="space-y-1.5 flex-1 flex flex-col">
                  <div className="space-y-1.5 flex-1">
                    {weeklyPlans[day].map((plan) => (
                      <button
                        key={plan.id}
                        onClick={() => {
                          setSelectedPlan(plan);
                          setDetailsOpen(true);
                        }}
                        className="text-left space-y-0.5 p-1.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <p className="text-[10px] font-semibold line-clamp-1">{plan.name}</p>
                        {plan.daily_calories && (
                          <div className="text-[9px] text-muted-foreground flex items-center gap-0.5">
                            <Flame className="w-2 h-2" />
                            {plan.daily_calories}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  <Button
                    onClick={() => handleDayClick(day)}
                    variant="outline"
                    size="sm"
                    className="w-full text-[10px] h-6 mt-1"
                  >
                    Change
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedDay && (
        <DayMealPlanDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          day={selectedDay}
          dayName={dayNames[dayOrder.indexOf(selectedDay)]}
          userId={clientId}
        />
      )}

      {selectedPlan && (
        <MealPlanDetailsDialog
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          plan={selectedPlan}
        />
      )}
    </div>
  );
}